import { useTranslation } from '@eti/providers';
import { css, cx, mediaQueries } from '@eti/styles';
import { Checkbox, Inline, LinkButton, Stack } from '@etg/wings';
import type { FilterType } from '@eti/schema-types';
import type { Filters } from 'providers/src/search/SearchContext';
import {
  ONE_DAY_STOPOVER,
  ONE_NIGHT_STOPOVER,
  SHORT_STOPOVER,
  STOPOVER_TYPES,
  TWO_NIGHTS_STOPOVER,
} from '../../../../constants/searchFiltersConstants';
import Filter from './Filter';

const filterStyles = css`
  @media ${mediaQueries.medium.down} {
    & header {
      padding-bottom: 0;
    }
  }
`;

const actionsWrapperStyles = css`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 4px;

  @media ${mediaQueries.large.up} {
    justify-content: flex-start;
    margin-bottom: 8px;
  }
`;

const actionStyles = css`
  display: inline-block;
  font-size: 0.875rem;
`;

const fieldSetStyles = css`
  border: none;
`;

const legendStyles = css`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

type StopoverType =
  | typeof SHORT_STOPOVER
  | typeof ONE_DAY_STOPOVER
  | typeof ONE_NIGHT_STOPOVER
  | typeof TWO_NIGHTS_STOPOVER;

type StopoverFilterType = {
  [key in StopoverType]?: boolean;
};

export interface FiltersWithStopoverTypes
  extends Omit<Filters, typeof FilterType.CheckedBaggageIncluded> {
  [FilterType.StopoverTypes]?: StopoverType[] | null;
}

interface StopoverTypesProps {
  className?: string;
  isInitiallyExpanded: boolean;
  isToggleable: boolean;
  onFilter: (filterOptions: { [key: string]: string[] | null | string }) => void;
  selectedFilters?: FiltersWithStopoverTypes | null;
}

const StopoverTypes = ({
  className,
  isInitiallyExpanded,
  isToggleable,
  onFilter,
  selectedFilters,
}: StopoverTypesProps) => {
  const { t } = useTranslation();
  const filters: StopoverFilterType = {
    [SHORT_STOPOVER]: selectedFilters?.[STOPOVER_TYPES]?.includes(SHORT_STOPOVER),
    [ONE_DAY_STOPOVER]: selectedFilters?.[STOPOVER_TYPES]?.includes(ONE_DAY_STOPOVER),
    [ONE_NIGHT_STOPOVER]: selectedFilters?.[STOPOVER_TYPES]?.includes(ONE_NIGHT_STOPOVER),
    [TWO_NIGHTS_STOPOVER]: selectedFilters?.[STOPOVER_TYPES]?.includes(TWO_NIGHTS_STOPOVER),
  };

  const iterableFilterKeys = Object.keys(filters) as Array<keyof StopoverFilterType>;

  let allStopoversSelected = iterableFilterKeys.every((stopover) => Boolean(filters[stopover]));

  const handleSelectOrDeselectAllStopovers = () => {
    allStopoversSelected = !allStopoversSelected;
    iterableFilterKeys.forEach((stopover) => {
      filters[stopover] = allStopoversSelected;
    });
    const selectedStopovers = iterableFilterKeys.filter((stopover) => filters[stopover]);

    onFilter({ [STOPOVER_TYPES]: selectedStopovers.length > 0 ? selectedStopovers : null });
  };

  const handleChange = (filter: StopoverType) => {
    filters[filter] = !filters[filter];
    allStopoversSelected = iterableFilterKeys.every((stopover) => filters[stopover]);
    const selectedStopovers = iterableFilterKeys.filter((stopover) => filters[stopover]);

    onFilter({ [STOPOVER_TYPES]: selectedStopovers.length > 0 ? selectedStopovers : null });
  };

  return (
    <Filter
      className={cx(className, filterStyles)}
      isInitiallyExpanded={isInitiallyExpanded}
      isToggleable={isToggleable}
      label={t('Searchform.Stopover.City')}
      name="VisitRome"
      shouldShowResetButton={false}
    >
      <div className={actionsWrapperStyles}>
        <LinkButton
          className={actionStyles}
          data-testid="resultPage-test-Filter-select-all-button"
          onClick={handleSelectOrDeselectAllStopovers}
        >
          {allStopoversSelected
            ? t('Result.SearchFilters.DeselectAll.Button')
            : t('Result.SearchFilters.SelectAll.Button')}
        </LinkButton>
      </div>
      <Stack as="fieldset" className={fieldSetStyles} spacing={8}>
        <legend className={legendStyles}>{t('Searchform.Stopover.LengthOfStay.Text')}</legend>
        <Inline spacing={8}>
          <Checkbox
            checked={filters[SHORT_STOPOVER] || allStopoversSelected}
            data-testid={`${SHORT_STOPOVER}-input`}
            id="shortStopCheckbox"
            name={SHORT_STOPOVER}
            onChange={() => handleChange(SHORT_STOPOVER)}
            value={filters[SHORT_STOPOVER] || allStopoversSelected ? 'true' : 'false'}
          />
          <Checkbox.Label htmlFor="shortStopCheckbox">
            {t('Searchform.Stopover.ShortStop')}
          </Checkbox.Label>
        </Inline>

        <Inline spacing={8}>
          <Checkbox
            checked={filters[ONE_DAY_STOPOVER] || allStopoversSelected}
            data-testid={`${ONE_DAY_STOPOVER}-input`}
            id="oneDayStopCheckbox"
            name={ONE_DAY_STOPOVER}
            onChange={() => handleChange(ONE_DAY_STOPOVER)}
            value={filters[ONE_DAY_STOPOVER] || allStopoversSelected ? 'true' : 'false'}
          />
          <Checkbox.Label htmlFor="oneDayStopCheckbox">
            {t('Searchform.Stopover.OneDayStop')}
          </Checkbox.Label>
        </Inline>

        <Inline spacing={8}>
          <Checkbox
            checked={filters[ONE_NIGHT_STOPOVER] || allStopoversSelected}
            data-testid={`${ONE_NIGHT_STOPOVER}-input`}
            id="oneNightStopCheckbox"
            name={ONE_NIGHT_STOPOVER}
            onChange={() => handleChange(ONE_NIGHT_STOPOVER)}
            value={filters[ONE_NIGHT_STOPOVER] || allStopoversSelected ? 'true' : 'false'}
          />
          <Checkbox.Label htmlFor="oneNightStopCheckbox">
            {t('Searchform.Stopover.OneNightStop')}
          </Checkbox.Label>
        </Inline>

        <Inline spacing={8}>
          <Checkbox
            checked={filters[TWO_NIGHTS_STOPOVER] || allStopoversSelected}
            data-testid={`${TWO_NIGHTS_STOPOVER}-input`}
            id="twoNightsStopCheckbox"
            name={TWO_NIGHTS_STOPOVER}
            onChange={() => handleChange(TWO_NIGHTS_STOPOVER)}
            value={filters[TWO_NIGHTS_STOPOVER] || allStopoversSelected ? 'true' : 'false'}
          />
          <Checkbox.Label htmlFor="twoNightsStopCheckbox">
            {t('Searchform.Stopover.TwoNightsStop')}
          </Checkbox.Label>
        </Inline>
      </Stack>
    </Filter>
  );
};

export default StopoverTypes;
